/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { ArrowUpRightIconOutline, ChevronRightIconOutline } from '../icons';

type TextLinkType =
  | 'internal'
  | 'internal-underline'
  | 'internal-icon'
  | 'external';

type TextLinkProps = {
  /** The content displayed inside the link */
  children: React.ReactNode;

  /** The URL the link points to */
  href?: string;

  /**
   * The type of the text link
   * - 'internal': a link to another page within the application
   * - 'internal-underline': a link to another page within the application with an underline and support for text color
   * - 'internal-icon': a link to another page within the application with an icon
   * - 'external': a link to an external website
   * @default 'internal'
   */
  type?: TextLinkType;

  /** Specifies where to open the linked document. If omitted and `type` is `"external"` it defaults to `"_blank"`. */
  target?: HTMLAnchorElement['target'];

  /** Additional class name */
  className?: string;
};

export const TextLink = <T extends React.ElementType = 'a'>({
  children,
  href,
  type = 'internal',
  as,
  className,
  target = type === 'external' ? '_blank' : '',
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, TextLinkProps>) => {
  const Component: React.ElementType = as ?? 'a';

  const classes = classNames('ndl-text-link', className, {
    'ndl-external-link': type === 'external',
    'ndl-internal-icon': type === 'internal-icon',
    'ndl-internal-underline': type === 'internal-underline',
  });

  return (
    <Component
      className={classes}
      href={href}
      ref={ref}
      style={style}
      target={target}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
      {type === 'external' && (
        <ArrowUpRightIconOutline className="ndl-external-link-icon" />
      )}
      {type === 'internal-icon' && (
        <ChevronRightIconOutline className="ndl-external-link-icon" />
      )}
    </Component>
  );
};
